import React, { useEffect, useMemo, useState } from 'react';
import Icon from 'src/components/common/Icon';
import {
    PortfolioProperty,
    PortfolioSlot,
} from 'src/redux/user_portfolio/user_portfolio.types';
import {
    derivePropertyVisibility,
    formatAddress,
    isFreeProperty,
    propertyTitle,
    VISIBILITY_LABEL,
} from './assignmentsHelpers';
import Pagination from './Pagination';
import {
    Badge,
    ClearLink,
    CountPill,
    FilterField,
    PropertiesPanelCard,
    PanelTitle,
    PanelTitleRow,
    PropertiesList,
    PropertyItem,
    PropertyRowTop,
    PropertyToolbarRow,
    SearchBox,
    SelectAllRow,
} from './VisibilityAssignment.styles';

/** Tipo MIME usado para arrastrar una o varias propiedades libres a un slot. */
export const PROPERTY_DND_TYPE = 'application/x-pcom-property-id';

/** Tamaños de página disponibles para la lista de propiedades. */
const PROPERTY_PAGE_SIZES = [10, 20, 50, 100];

/** Opciones del filtro por estado de la propiedad. */
type StatusFilter = 'todas' | 'libre' | 'prime' | 'destacado';

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
    { value: 'todas', label: 'Todas' },
    { value: 'libre', label: 'Disponibles para asignar' },
    { value: 'prime', label: 'Asignadas a Prime' },
    { value: 'destacado', label: 'Asignadas a Destacados' },
];

interface PropertiesPanelProps {
    properties: PortfolioProperty[];
    slots: PortfolioSlot[];
    total: number;
    selectedPropertyIds: number[];
    onToggleProperty: (id: number) => void;
    onSelectAllFree: (freeIds: number[], checked: boolean) => void;
    onClearSelection: () => void;
    onDragStateChange: (id: number | null) => void;
}

/**
 * Construye un "ghost" temporal para el arrastre múltiple (varias propiedades),
 * tal como el diseño: un círculo con el conteo y "Selección múltiple".
 */
const buildDragGhost = (count: number): HTMLElement => {
    const el = document.createElement('div');
    el.style.cssText = [
        'position:absolute',
        'top:-1000px',
        'left:-1000px',
        'display:flex',
        'align-items:center',
        'gap:10px',
        'padding:10px 14px',
        'background:#ffffff',
        'border:1px solid #d9d9d9',
        'border-radius:12px',
        'box-shadow:0 10px 28px rgba(0,0,0,0.18)',
        'font-family:Roboto',
    ].join(';');
    el.innerHTML = `
        <span style="display:inline-flex;align-items:center;justify-content:center;min-width:34px;height:34px;padding:0 8px;border-radius:999px;background:#3454d1;color:#fff;font-weight:800;font-size:14px;">${count}</span>
        <span style="display:flex;flex-direction:column;">
            <span style="font-size:13px;font-weight:700;color:#1f1f1f;">${count} propiedades</span>
            <span style="font-size:11px;color:#8a8a8a;">Selección múltiple</span>
        </span>`;
    document.body.appendChild(el);
    return el;
};

const PropertiesPanel = ({
    properties,
    slots,
    total,
    selectedPropertyIds,
    onToggleProperty,
    onSelectAllFree,
    onClearSelection,
    onDragStateChange,
}: PropertiesPanelProps) => {
    const [query, setQuery] = useState('');
    const [status, setStatus] = useState<StatusFilter>('todas');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PROPERTY_PAGE_SIZES[0]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return properties.filter((property) => {
            if (q) {
                const haystack = `${property.id_property} ${formatAddress(
                    property.address_array
                )}`.toLowerCase();
                if (!haystack.includes(q)) return false;
            }
            if (status !== 'todas') {
                const visibility = derivePropertyVisibility(property, slots);
                if (status === 'libre' && visibility !== 'libre') return false;
                if (status === 'prime' && visibility !== 'prime') return false;
                if (status === 'destacado' && visibility !== 'destacado') return false;
            }
            return true;
        });
    }, [properties, slots, query, status]);

    // Con búsqueda o filtro activo el contador refleja los resultados; sin
    // ellos muestra el total de propiedades del anunciante.
    const count = query.trim() || status !== 'todas' ? filtered.length : total;

    // Al cambiar búsqueda, filtro o tamaño de página se vuelve a la primera página.
    useEffect(() => {
        setPage(1);
    }, [query, status, pageSize]);

    const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(page, pageCount);
    useEffect(() => {
        if (page !== safePage) setPage(safePage);
    }, [page, safePage]);

    const pageItems = useMemo(
        () => filtered.slice((safePage - 1) * pageSize, safePage * pageSize),
        [filtered, safePage, pageSize]
    );

    // "Seleccionar todas" opera sobre las propiedades libres de la página actual.
    const freeIds = useMemo(
        () => pageItems.filter((p) => isFreeProperty(p, slots)).map((p) => p.id_property),
        [pageItems, slots]
    );
    const allFreeSelected =
        freeIds.length > 0 && freeIds.every((id) => selectedPropertyIds.includes(id));
    const selectedCount = selectedPropertyIds.length;

    const handleDragStart = (e: React.DragEvent, id: number) => {
        const inSelection = selectedPropertyIds.includes(id);
        const dragIds = inSelection && selectedCount > 1 ? selectedPropertyIds : [id];
        const payload = dragIds.join(',');
        e.dataTransfer.setData(PROPERTY_DND_TYPE, payload);
        e.dataTransfer.setData('text/plain', payload);
        e.dataTransfer.effectAllowed = 'move';
        if (dragIds.length > 1) {
            const ghost = buildDragGhost(dragIds.length);
            e.dataTransfer.setDragImage(ghost, 24, 24);
            window.setTimeout(() => document.body.removeChild(ghost), 0);
        }
        onDragStateChange(id);
    };

    return (
        <PropertiesPanelCard as="section" aria-label="Propiedades del anunciante">
            <PanelTitleRow>
                <PanelTitle>Propiedades</PanelTitle>
                <CountPill data-id="properties-count">{count}</CountPill>
            </PanelTitleRow>

            <SearchBox>
                <Icon icon="pcom-icon-search" size={16} />
                <input
                    type="text"
                    data-id="properties-search"
                    placeholder="Buscar propiedades..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
            </SearchBox>

            <FilterField>
                <span className="filter-label">Estado</span>
                <select
                    data-id="properties-status-filter"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as StatusFilter)}
                >
                    {STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </FilterField>

            {freeIds.length > 0 && (
                <PropertyToolbarRow>
                    <SelectAllRow as="label" style={{ margin: 0 }}>
                        <input
                            type="checkbox"
                            data-id="select-all-free-properties"
                            checked={allFreeSelected}
                            onChange={(e) => onSelectAllFree(freeIds, e.target.checked)}
                        />
                        Seleccionar todas (página actual)
                    </SelectAllRow>
                    <ClearLink
                        type="button"
                        data-id="clear-property-selection"
                        disabled={selectedCount === 0}
                        onClick={onClearSelection}
                    >
                        Limpiar ({selectedCount})
                    </ClearLink>
                </PropertyToolbarRow>
            )}

            <PropertiesList>
                {filtered.length === 0 ? (
                    <span style={{ fontSize: 12, color: '#9a9a9a' }}>
                        {properties.length === 0
                            ? 'Este anunciante no tiene propiedades.'
                            : 'Sin resultados para la búsqueda.'}
                    </span>
                ) : (
                    pageItems.map((property) => {
                        const visibility = derivePropertyVisibility(property, slots);
                        const free = isFreeProperty(property, slots);
                        const selected = selectedPropertyIds.includes(property.id_property);
                        return (
                            <PropertyItem
                                as="div"
                                key={property.id_property}
                                data-id={`property-item-${property.id_property}`}
                                $selected={selected}
                                $free={free}
                                draggable={free}
                                role={free ? 'button' : undefined}
                                aria-pressed={free ? selected : undefined}
                                onClick={() => {
                                    if (free) onToggleProperty(property.id_property);
                                }}
                                onDragStart={(e) => {
                                    if (!free) {
                                        e.preventDefault();
                                        return;
                                    }
                                    handleDragStart(e, property.id_property);
                                }}
                                onDragEnd={() => onDragStateChange(null)}
                            >
                                <PropertyRowTop>
                                    {free && (
                                        <input
                                            type="checkbox"
                                            data-id={`property-checkbox-${property.id_property}`}
                                            checked={selected}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={() => onToggleProperty(property.id_property)}
                                            aria-label={`Seleccionar propiedad ${property.id_property}`}
                                        />
                                    )}
                                    <span className="property-name">{propertyTitle(property)}</span>
                                </PropertyRowTop>
                                <span className="property-meta">
                                    ID {property.id_property}
                                    {property.address_array?.city
                                        ? ` · ${property.address_array.city}`
                                        : ''}
                                </span>
                                <Badge $variant={visibility}>{VISIBILITY_LABEL[visibility]}</Badge>
                            </PropertyItem>
                        );
                    })
                )}
            </PropertiesList>

            {filtered.length > 0 && (
                <Pagination
                    dataId="properties-pagination"
                    page={safePage}
                    total={filtered.length}
                    pageSize={pageSize}
                    onPageChange={setPage}
                    pageSizeOptions={PROPERTY_PAGE_SIZES}
                    onPageSizeChange={setPageSize}
                />
            )}
        </PropertiesPanelCard>
    );
};

export default PropertiesPanel;
